/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Variant-serving AvatarImage
 */

import React from "react";
import type { ComponentProps } from "react";
import { AvatarImage } from "@lemonadetraining/web-ui";
// Deliberately imported by PACKAGE name, not a relative path: every Jest
// consumer then resolves the hook through the single moduleNameMapper
// passthrough — the real hook's import chain initializes Firebase at module
// scope, which jsdom suites must never load.
import { useImageVariant } from "@lemonadetraining/web-shared/hooks/useImageVariant";

/**
 * Drop-in AvatarImage that serves the 200px image variant: the original
 * renders immediately, the PRELOADED variant swaps in without ever flashing
 * the fallback, and a failed variant swaps back — the avatar is never
 * broken and first paint never waits on a storage probe. Renders nothing
 * while there is no src, so the surrounding Avatar's fallback shows exactly
 * as before.
 */
/* `crossOrigin` is also withheld: the no-flash guarantee relies on the
 * avatar's request reusing the hook's no-cors preload cache entry — a
 * CORS-mode request would split the cache key and resurrect the flash. */
type VariantAvatarImageProps = Omit<
  ComponentProps<typeof AvatarImage>,
  "src" | "onLoadingStatusChange" | "crossOrigin"
> & {
  src?: string | null;
};

const VariantAvatarImage = ({ src, ...props }: VariantAvatarImageProps) => {
  const variant = useImageVariant(src ?? null, "200x200", "avatar");
  if (!variant.src) return null;
  return (
    <AvatarImage
      {...props}
      src={variant.src}
      // The avatar primitive only mounts the <img> once its probe LOADS — a
      // failed src never fires DOM onError, so the status callback is the
      // only reliable seam for the swap-back. Placed after the props spread
      // so a caller can never silently replace it.
      onLoadingStatusChange={(status) => {
        if (status === "error") variant.onError();
      }}
    />
  );
};

export default VariantAvatarImage;
